// Parts tab in ticket detail — shows part rows and inline add form.
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Trash2 } from "lucide-react";
import { repairApi, RepairPart, fmtVND } from "../repair-api";
import { apiGet } from "@/lib/api/client";

interface Props {
  ticketId: string;
  parts: RepairPart[];
  disabled?: boolean; // true when terminal status
}

interface VariantHit {
  id: string;
  sku: string;
  productName: string;
}

export function PartsSection({ ticketId, parts, disabled }: Props) {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [hits, setHits] = useState<VariantHit[]>([]);
  const [form, setForm] = useState({ variantId: "", variantLabel: "", quantity: 1, unitPrice: "", note: "" });

  const add = useMutation({
    mutationFn: () =>
      repairApi.addPart(ticketId, {
        variantId: form.variantId,
        quantity: form.quantity,
        unitPrice: form.unitPrice || "0",
        note: form.note || undefined,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["repair-ticket", ticketId] });
      setForm({ variantId: "", variantLabel: "", quantity: 1, unitPrice: "", note: "" });
      setSearch("");
      setHits([]);
    },
  });

  const remove = useMutation({
    mutationFn: (partId: string) => repairApi.deletePart(partId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["repair-ticket", ticketId] }),
  });

  async function searchVariants(q: string) {
    setSearch(q);
    if (q.length < 2) { setHits([]); return; }
    try {
      const res = await apiGet<VariantHit[]>("/v1/products/variants/search", { q, limit: 8 });
      setHits(res);
    } catch { setHits([]); }
  }

  function selectVariant(v: VariantHit) {
    setForm((f) => ({ ...f, variantId: v.id, variantLabel: `${v.productName} (${v.sku})` }));
    setSearch(v.productName + " " + v.sku);
    setHits([]);
  }

  const total = parts.reduce((sum, p) => sum + parseFloat(p.lineTotal || "0"), 0);

  return (
    <div className="space-y-4">
      {/* Part list */}
      {parts.length > 0 ? (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-muted-foreground text-xs">
              <th className="pb-2 text-left">Phụ tùng</th>
              <th className="pb-2 text-right">SL</th>
              <th className="pb-2 text-right">Đơn giá</th>
              <th className="pb-2 text-right">Thành tiền</th>
              <th className="pb-2 text-right">Kho</th>
              <th className="pb-2" />
            </tr>
          </thead>
          <tbody className="divide-y">
            {parts.map((p) => (
              <tr key={p.id}>
                <td className="py-2">
                  <span className="font-medium">{p.productName}</span>
                  {p.variantSku && <span className="ml-1 text-xs text-muted-foreground">({p.variantSku})</span>}
                  {p.note && <p className="text-xs text-muted-foreground">{p.note}</p>}
                </td>
                <td className="py-2 text-right">{p.quantity}</td>
                <td className="py-2 text-right">{fmtVND(p.unitPrice)}₫</td>
                <td className="py-2 text-right font-medium">{fmtVND(p.lineTotal)}₫</td>
                <td className="py-2 text-right">
                  <span className={`text-[10px] rounded-full px-1.5 py-0.5 ${p.stockApplied ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}`}>
                    {p.stockApplied ? "Đã trừ" : "Chờ"}
                  </span>
                </td>
                <td className="py-2 text-right">
                  {!disabled && (
                    <button
                      onClick={() => remove.mutate(p.id)}
                      disabled={remove.isPending}
                      className="text-destructive hover:opacity-70 disabled:opacity-40"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t font-semibold text-sm">
              <td colSpan={3} className="pt-2 text-right text-muted-foreground">Tổng phụ tùng:</td>
              <td className="pt-2 text-right">{total.toLocaleString("vi-VN")}₫</td>
              <td colSpan={2} />
            </tr>
          </tfoot>
        </table>
      ) : (
        <p className="text-sm text-muted-foreground py-4 text-center">Chưa có phụ tùng</p>
      )}

      {/* Add form */}
      {!disabled && (
        <div className="rounded-lg border bg-muted/30 p-4 space-y-3">
          <p className="text-sm font-medium">Thêm phụ tùng</p>

          {/* Variant search */}
          <div className="relative">
            <input
              type="text"
              placeholder="Tìm sản phẩm / SKU..."
              className="w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
              value={search}
              onChange={(e) => searchVariants(e.target.value)}
            />
            {hits.length > 0 && (
              <ul className="absolute z-10 mt-1 w-full rounded-md border bg-card shadow-lg">
                {hits.map((v) => (
                  <li
                    key={v.id}
                    className="cursor-pointer px-3 py-2 text-sm hover:bg-accent"
                    onMouseDown={() => selectVariant(v)}
                  >
                    {v.productName} <span className="text-muted-foreground">({v.sku})</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="flex gap-2">
            <label className="flex-1 space-y-1 text-xs">
              <span className="text-muted-foreground">Số lượng</span>
              <input
                type="number" min={1}
                className="w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none"
                value={form.quantity}
                onChange={(e) => setForm((f) => ({ ...f, quantity: parseInt(e.target.value) || 1 }))}
              />
            </label>
            <label className="flex-1 space-y-1 text-xs">
              <span className="text-muted-foreground">Đơn giá (VNĐ)</span>
              <input
                type="number" min={0}
                className="w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none"
                value={form.unitPrice}
                onChange={(e) => setForm((f) => ({ ...f, unitPrice: e.target.value }))}
              />
            </label>
          </div>

          <input
            type="text"
            placeholder="Ghi chú (tuỳ chọn)"
            className="w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none"
            value={form.note}
            onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
          />

          {add.isError && <p className="text-xs text-destructive">Lỗi khi thêm phụ tùng</p>}

          <button
            onClick={() => add.mutate()}
            disabled={!form.variantId || add.isPending}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-40"
          >
            {add.isPending ? "Đang thêm..." : "Thêm phụ tùng"}
          </button>
        </div>
      )}
    </div>
  );
}
